/**
 * CouponDefaultTemplateCard
 *
 * Collapsed editor card for a global coupon default template ("regular" or
 * "access"), shown at the bottom of the admin Coupons page. Mirrors the
 * collapsed "Default pack pricing" card on AffiliatesAdminPage: closed by default,
 * a Show/Hide toggle in the header, and the template is only fetched once the
 * card is opened.
 *
 * - kind "regular": full regular-coupon template (discount percent XOR
 *   amount+currency, renewals, trial days, max redemptions, first-time only,
 *   plans, billing periods, optional free-access pair) plus a digits-only
 *   `suffix` — applied codes are USERNAME + suffix. Never an access coupon,
 *   and it has no code/name field.
 * - kind "access": free-access plan + (days XOR until), required max
 *   redemptions, first-time only, plus `default_count` (how many access
 *   coupons a batch creates by default, 1–200).
 */

import React, { useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Divider,
  FormField,
  Input,
  PrimaryButton,
  RadioGroup,
  SecondaryButton,
  Select,
} from "../../design-system";
import { textColors, typography } from "../../design-system/tokens";
import { useCouponDefault, useSaveCouponDefault } from "../hooks/useAdminCouponDefaults";

const DEFAULT_CURRENCY = "USD";
const SUFFIX_MAX_LENGTH = 10;
const ACCESS_DEFAULT_COUNT_MAX = 200;

// Status color patterns (explicit dark variants allowed for semantic status states)
const noticeStyles = {
  success: "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
  error: "bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
};

const KIND_COPY = {
  regular: {
    title: "Default coupon",
    subtitle: "Template for the default regular coupon applied per user. Applied codes are USERNAME + suffix.",
    showLabel: "Show default coupon",
    hideLabel: "Hide default coupon",
    saveLabel: "Save default coupon",
    savedMessage: "Default coupon saved.",
  },
  access: {
    title: "Default access coupon",
    subtitle: "Template for default access-coupon batches (free plan access codes).",
    showLabel: "Show default access coupon",
    hideLabel: "Hide default access coupon",
    saveLabel: "Save default access coupon",
    savedMessage: "Default access coupon saved.",
  },
};

const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toggleArrayValue = (arr, value) => {
  const next = new Set(arr || []);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return Array.from(next);
};

const toRegularDraft = (template) => {
  const t = template || null;
  let discountType = "percent";
  if (t) {
    if (t.percent_off != null) discountType = "percent";
    else if (t.amount_off != null) discountType = "amount";
    else discountType = "trial";
  }
  return {
    discountType,
    percent_off: t?.percent_off != null ? String(t.percent_off) : "",
    amount_off: t?.amount_off != null ? String(t.amount_off) : "",
    currency: t?.currency || DEFAULT_CURRENCY,
    include_renewals: Boolean(t?.include_renewals),
    free_trial_days: t?.free_trial_days != null ? String(t.free_trial_days) : "0",
    max_redemptions: t?.max_redemptions != null ? String(t.max_redemptions) : "",
    first_time_only: Boolean(t?.first_time_only),
    plans: Array.isArray(t?.plans) ? [...t.plans] : [],
    billing_periods: Array.isArray(t?.billing_periods) ? [...t.billing_periods] : [],
    free_access_mode: t?.free_access_until ? "until" : t?.free_access_days ? "days" : "none",
    free_access_until: isoToLocalInput(t?.free_access_until),
    free_access_days: t?.free_access_days != null ? String(t.free_access_days) : "",
    free_access_plan: t?.free_access_plan || "",
    suffix: t?.suffix != null ? String(t.suffix) : "",
  };
};

const toAccessDraft = (template) => {
  const t = template || null;
  return {
    free_access_mode: t?.free_access_until ? "until" : "days",
    free_access_until: isoToLocalInput(t?.free_access_until),
    free_access_days: t?.free_access_days != null ? String(t.free_access_days) : "",
    free_access_plan: t?.free_access_plan || "",
    max_redemptions: t?.max_redemptions != null ? String(t.max_redemptions) : "",
    first_time_only: Boolean(t?.first_time_only),
    default_count: t?.default_count != null ? String(t.default_count) : "",
  };
};

const validateFreeAccessPair = (draft, { required }) => {
  const errors = {};
  const mode = draft.free_access_mode;
  if (mode === "none") {
    if (required) errors.free_access_mode = "The default access coupon must grant free access.";
    return errors;
  }
  if (!draft.free_access_plan) errors.free_access_plan = "Select a free access plan.";
  if (mode === "until") {
    if (!draft.free_access_until) {
      errors.free_access_until = "Set a free access date.";
    } else {
      const until = new Date(draft.free_access_until);
      if (Number.isNaN(until.getTime())) errors.free_access_until = "Invalid date.";
      else if (until.getTime() <= Date.now()) errors.free_access_until = "Free access until must be in the future.";
    }
  } else if (mode === "days") {
    const days = Number(draft.free_access_days);
    if (!draft.free_access_days) errors.free_access_days = "Set the number of free access days.";
    else if (!Number.isFinite(days) || days < 1) errors.free_access_days = "Free access days must be at least 1.";
  }
  return errors;
};

const validateRegularDraft = (draft) => {
  const errors = { ...validateFreeAccessPair(draft, { required: false }) };

  if (draft.discountType === "percent") {
    const v = Number(draft.percent_off);
    if (!draft.percent_off) errors.percent_off = "Percent off is required.";
    else if (Number.isNaN(v) || v <= 0 || v > 100) errors.percent_off = "Percent off must be between 0 and 100.";
  } else if (draft.discountType === "amount") {
    const v = Number(draft.amount_off);
    if (!draft.amount_off) errors.amount_off = "Amount off is required.";
    else if (Number.isNaN(v) || v <= 0) errors.amount_off = "Amount off must be > 0.";
    if (!draft.currency?.trim()) errors.currency = "Currency is required for amount-off coupons.";
  } else {
    const trialDays = Number(draft.free_trial_days || 0);
    if (!Number.isFinite(trialDays) || trialDays <= 0) {
      errors.free_trial_days = "Free trial period must be > 0 for trial-only coupons.";
    }
  }

  if (draft.max_redemptions) {
    const n = Number(draft.max_redemptions);
    if (!Number.isInteger(n) || n < 1) errors.max_redemptions = "Max redemptions must be a whole number of 1 or more.";
  }

  return errors;
};

const validateAccessDraft = (draft) => {
  const errors = { ...validateFreeAccessPair(draft, { required: true }) };

  const maxN = Number(draft.max_redemptions);
  if (!draft.max_redemptions || !Number.isInteger(maxN) || maxN < 1) {
    errors.max_redemptions = "Max redemptions is required (1 or more).";
  }

  const countN = Number(draft.default_count);
  if (!draft.default_count || !Number.isInteger(countN) || countN < 1 || countN > ACCESS_DEFAULT_COUNT_MAX) {
    errors.default_count = `Default count must be between 1 and ${ACCESS_DEFAULT_COUNT_MAX}.`;
  }

  return errors;
};

const buildFreeAccessPayload = (draft) => ({
  free_access_plan: draft.free_access_mode !== "none" && draft.free_access_plan ? draft.free_access_plan : null,
  free_access_days:
    draft.free_access_mode === "days" && draft.free_access_days ? Number(draft.free_access_days) : null,
  free_access_until:
    draft.free_access_mode === "until" && draft.free_access_until
      ? new Date(draft.free_access_until).toISOString()
      : null,
});

// Template field names must match the backend exactly (see adminApi docs).
const buildRegularPayload = (draft) => ({
  percent_off: draft.discountType === "percent" && draft.percent_off !== "" ? Number(draft.percent_off) : null,
  amount_off: draft.discountType === "amount" && draft.amount_off !== "" ? Number(draft.amount_off) : null,
  currency:
    draft.discountType === "amount" && draft.amount_off !== ""
      ? (draft.currency || DEFAULT_CURRENCY).trim().toUpperCase()
      : null,
  include_renewals: Boolean(draft.include_renewals),
  free_trial_days: Number(draft.free_trial_days || 0),
  max_redemptions: draft.max_redemptions ? Number(draft.max_redemptions) : null,
  first_time_only: Boolean(draft.first_time_only),
  plans: draft.plans || [],
  billing_periods: draft.billing_periods || [],
  ...buildFreeAccessPayload(draft),
  suffix: draft.suffix || "",
});

const buildAccessPayload = (draft) => ({
  ...buildFreeAccessPayload(draft),
  max_redemptions: Number(draft.max_redemptions),
  first_time_only: Boolean(draft.first_time_only),
  default_count: Number(draft.default_count),
});

const CouponDefaultTemplateCard = ({ kind }) => {
  const copy = KIND_COPY[kind];
  const idPrefix = `default-${kind}`;
  const panelId = `${idPrefix}-template-panel`;

  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState(null); // { type: 'success'|'error', message }

  const templateQuery = useCouponDefault(kind, { enabled: open });
  const saveMutation = useSaveCouponDefault();

  const isLoaded = Boolean(templateQuery.data);
  const isConfigured = Boolean(templateQuery.data?.configured);
  const template = templateQuery.data?.template || null;

  const toDraft = kind === "regular" ? toRegularDraft : toAccessDraft;
  const [draft, setDraft] = useState(() => toDraft(template));

  // Reset the draft whenever the loaded template changes (initial load, refetch
  // after save). Done during render — React's "derived state from props"
  // pattern — the same way CouponPackPricingEditor resets its rows.
  const templateSignature = JSON.stringify(template);
  const [lastTemplateSignature, setLastTemplateSignature] = useState(templateSignature);
  if (lastTemplateSignature !== templateSignature) {
    setLastTemplateSignature(templateSignature);
    setDraft(toDraft(template));
    setErrors({});
  }

  const setField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleFreeAccessModeChange = (value) => {
    setDraft((prev) => ({
      ...prev,
      free_access_mode: value,
      // Clear the non-selected inputs so stale values don't leak into the payload.
      free_access_until: value === "until" ? prev.free_access_until : "",
      free_access_days: value === "days" ? prev.free_access_days : "",
      free_access_plan: value === "none" ? "" : prev.free_access_plan,
    }));
    setErrors((prev) => ({
      ...prev,
      free_access_mode: undefined,
      free_access_until: undefined,
      free_access_days: undefined,
      free_access_plan: undefined,
    }));
  };

  const handleSave = async () => {
    setNotice(null);
    const validationErrors = kind === "regular" ? validateRegularDraft(draft) : validateAccessDraft(draft);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      const payload = kind === "regular" ? buildRegularPayload(draft) : buildAccessPayload(draft);
      await saveMutation.mutateAsync({ kind, template: payload });
      setNotice({ type: "success", message: copy.savedMessage });
    } catch (err) {
      setNotice({ type: "error", message: err?.message || "Failed to save the default template." });
    }
  };

  const freeAccessDateOrDaysFields = (
    <>
      {draft.free_access_mode === "until" ? (
        <FormField
          label="Free access until"
          htmlFor={`${idPrefix}-free-access-until`}
          required
          helperText="Access ends at this date for everyone who redeems."
        >
          <Input
            id={`${idPrefix}-free-access-until`}
            type="datetime-local"
            value={draft.free_access_until}
            onChange={(e) => setField("free_access_until", e.target.value)}
            error={errors.free_access_until}
          />
        </FormField>
      ) : draft.free_access_mode === "days" ? (
        <FormField
          label="Free access days"
          htmlFor={`${idPrefix}-free-access-days`}
          required
          helperText="Access lasts this many days from the moment the coupon is redeemed."
        >
          <Input
            id={`${idPrefix}-free-access-days`}
            type="number"
            min="1"
            value={draft.free_access_days}
            onChange={(e) => setField("free_access_days", e.target.value)}
            error={errors.free_access_days}
            placeholder="e.g. 30"
          />
        </FormField>
      ) : (
        <div className="hidden md:block" aria-hidden="true" />
      )}

      {draft.free_access_mode !== "none" ? (
        <FormField
          label="Free access plan"
          htmlFor={`${idPrefix}-free-access-plan`}
          required
          helperText="Paid plan granted for free during the free-access period."
        >
          <Select
            id={`${idPrefix}-free-access-plan`}
            value={draft.free_access_plan}
            onChange={(e) => setField("free_access_plan", e.target.value)}
            options={[
              { value: "", label: "Select a plan" },
              { value: "plus", label: "Plus" },
              { value: "pro", label: "Pro" },
            ]}
            placeholder={null}
            error={errors.free_access_plan}
          />
        </FormField>
      ) : (
        <div className="hidden md:block" aria-hidden="true" />
      )}
    </>
  );

  return (
    <Card variant="glass">
      <CardHeader
        title={copy.title}
        subtitle={copy.subtitle}
        actions={
          <SecondaryButton
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls={panelId}
          >
            {open ? copy.hideLabel : copy.showLabel}
          </SecondaryButton>
        }
      />
      {open && (
        <CardBody id={panelId} className="space-y-5">
          {templateQuery.isLoading ? (
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>Loading default template…</p>
          ) : templateQuery.error ? (
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>
              Failed to load default template: {templateQuery.error?.message || "Unknown error"}
            </p>
          ) : (
            <>
              {isLoaded && !isConfigured && (
                <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                  No default is configured yet. Fill in the template and save to create one.
                </p>
              )}

              {kind === "regular" ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <FormField
                      label="Suffix"
                      htmlFor={`${idPrefix}-suffix`}
                      helperText="Digits only (max 10). Applied codes will be USERNAME + suffix."
                    >
                      <Input
                        id={`${idPrefix}-suffix`}
                        value={draft.suffix}
                        onChange={(e) =>
                          setField("suffix", e.target.value.replace(/\D/g, "").slice(0, SUFFIX_MAX_LENGTH))
                        }
                        maxLength={SUFFIX_MAX_LENGTH}
                        placeholder="e.g. 10"
                        error={errors.suffix}
                      />
                    </FormField>

                    <FormField
                      label="Max redemptions"
                      htmlFor={`${idPrefix}-max-redemptions`}
                      optional
                      helperText="Optional. Leave empty for unlimited redemptions."
                    >
                      <Input
                        id={`${idPrefix}-max-redemptions`}
                        type="number"
                        min="1"
                        value={draft.max_redemptions}
                        onChange={(e) => setField("max_redemptions", e.target.value)}
                        error={errors.max_redemptions}
                        placeholder="e.g. 100"
                      />
                    </FormField>

                    <FormField
                      label="Free trial period (days)"
                      htmlFor={`${idPrefix}-free-trial-days`}
                      helperText="Optional. Applies only on free → paid checkout. Delays the first invoice by this many days."
                    >
                      <Input
                        id={`${idPrefix}-free-trial-days`}
                        type="number"
                        min="0"
                        value={draft.free_trial_days}
                        onChange={(e) => setField("free_trial_days", e.target.value)}
                        error={errors.free_trial_days}
                      />
                    </FormField>

                    <FormField
                      label="Free access"
                      htmlFor={`${idPrefix}-free-access-mode`}
                      optional
                      helperText="Optional. Grant a paid plan for free — until a fixed date or for a number of days from redemption."
                    >
                      <Select
                        id={`${idPrefix}-free-access-mode`}
                        value={draft.free_access_mode}
                        onChange={(e) => handleFreeAccessModeChange(e.target.value)}
                        options={[
                          { value: "none", label: "None" },
                          { value: "until", label: "Until a date" },
                          { value: "days", label: "For a number of days" },
                        ]}
                        placeholder={null}
                        error={errors.free_access_mode}
                      />
                    </FormField>

                    {freeAccessDateOrDaysFields}
                  </div>

                  <Divider />

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-1">
                      <FormField label="Discount type" htmlFor={`${idPrefix}-discount-type`}>
                        <RadioGroup
                          name={`${idPrefix}-discount-type`}
                          value={draft.discountType}
                          onChange={(value) => setField("discountType", value)}
                          options={[
                            { value: "percent", label: "Percent off" },
                            { value: "amount", label: "Amount off" },
                            { value: "trial", label: "Free trial only" },
                          ]}
                          orientation="vertical"
                          aria-label={`${copy.title} discount type`}
                        />
                      </FormField>
                    </div>

                    <div className="lg:col-span-2">
                      {draft.discountType === "trial" ? (
                        <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                          This coupon provides a free trial period (no price discount).
                        </div>
                      ) : draft.discountType === "percent" ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField label="Percent off" htmlFor={`${idPrefix}-percent-off`} required>
                            <Input
                              id={`${idPrefix}-percent-off`}
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={draft.percent_off}
                              onChange={(e) => setField("percent_off", e.target.value)}
                              error={errors.percent_off}
                              placeholder="e.g. 10"
                            />
                          </FormField>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField label="Amount off" htmlFor={`${idPrefix}-amount-off`} required>
                            <Input
                              id={`${idPrefix}-amount-off`}
                              type="number"
                              min="0"
                              step="0.01"
                              value={draft.amount_off}
                              onChange={(e) => setField("amount_off", e.target.value)}
                              error={errors.amount_off}
                              placeholder="e.g. 5.00"
                            />
                          </FormField>
                          <FormField label="Currency" htmlFor={`${idPrefix}-currency`} required>
                            <Input
                              id={`${idPrefix}-currency`}
                              value={draft.currency}
                              onChange={(e) => setField("currency", e.target.value)}
                              error={errors.currency}
                              placeholder="USD"
                            />
                          </FormField>
                        </div>
                      )}
                    </div>
                  </div>

                  <Divider />

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <Card className="lg:col-span-1">
                      <CardBody className="space-y-3">
                        <p className={`${typography.label} ${textColors.secondary}`}>Options</p>
                        <Checkbox
                          checked={draft.include_renewals}
                          onChange={(e) => setField("include_renewals", e.target.checked)}
                          label="Include renewals"
                        />
                        <Checkbox
                          checked={draft.first_time_only}
                          onChange={(e) => setField("first_time_only", e.target.checked)}
                          label="First-time only"
                        />
                      </CardBody>
                    </Card>

                    <Card className="lg:col-span-1">
                      <CardBody className="space-y-3">
                        <p className={`${typography.label} ${textColors.secondary}`}>Plans (optional)</p>
                        <Checkbox
                          checked={(draft.plans || []).includes("free")}
                          onChange={() => setField("plans", toggleArrayValue(draft.plans, "free"))}
                          label="Free"
                        />
                        <Checkbox
                          checked={(draft.plans || []).includes("plus")}
                          onChange={() => setField("plans", toggleArrayValue(draft.plans, "plus"))}
                          label="Plus"
                        />
                        <Checkbox
                          checked={(draft.plans || []).includes("pro")}
                          onChange={() => setField("plans", toggleArrayValue(draft.plans, "pro"))}
                          label="Pro"
                        />
                        <p className={`${typography.caption} ${textColors.muted}`}>Leave empty for all plans.</p>
                      </CardBody>
                    </Card>

                    <Card className="lg:col-span-1">
                      <CardBody className="space-y-3">
                        <p className={`${typography.label} ${textColors.secondary}`}>Billing periods (optional)</p>
                        <Checkbox
                          checked={(draft.billing_periods || []).includes("monthly")}
                          onChange={() =>
                            setField("billing_periods", toggleArrayValue(draft.billing_periods, "monthly"))
                          }
                          label="Monthly"
                        />
                        <Checkbox
                          checked={(draft.billing_periods || []).includes("yearly")}
                          onChange={() =>
                            setField("billing_periods", toggleArrayValue(draft.billing_periods, "yearly"))
                          }
                          label="Yearly"
                        />
                        <p className={`${typography.caption} ${textColors.muted}`}>
                          Leave empty for all billing periods.
                        </p>
                      </CardBody>
                    </Card>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <FormField
                      label="Free access"
                      htmlFor={`${idPrefix}-free-access-mode`}
                      required
                      helperText="Grant a paid plan for free — until a fixed date or for a number of days from redemption."
                    >
                      <Select
                        id={`${idPrefix}-free-access-mode`}
                        value={draft.free_access_mode}
                        onChange={(e) => handleFreeAccessModeChange(e.target.value)}
                        options={[
                          { value: "days", label: "For a number of days" },
                          { value: "until", label: "Until a date" },
                        ]}
                        placeholder={null}
                        error={errors.free_access_mode}
                      />
                    </FormField>

                    {freeAccessDateOrDaysFields}

                    <FormField
                      label="Max redemptions"
                      htmlFor={`${idPrefix}-max-redemptions`}
                      required
                      helperText="Required for access coupons (1 or more redemptions per code)."
                    >
                      <Input
                        id={`${idPrefix}-max-redemptions`}
                        type="number"
                        min="1"
                        value={draft.max_redemptions}
                        onChange={(e) => setField("max_redemptions", e.target.value)}
                        error={errors.max_redemptions}
                        placeholder="e.g. 1"
                      />
                    </FormField>

                    <FormField
                      label="Default coupons per batch"
                      htmlFor={`${idPrefix}-default-count`}
                      required
                      helperText={`Default number of access coupons created per batch (1–${ACCESS_DEFAULT_COUNT_MAX}).`}
                    >
                      <Input
                        id={`${idPrefix}-default-count`}
                        type="number"
                        min="1"
                        max={ACCESS_DEFAULT_COUNT_MAX}
                        value={draft.default_count}
                        onChange={(e) => setField("default_count", e.target.value)}
                        error={errors.default_count}
                        placeholder="e.g. 5"
                      />
                    </FormField>
                  </div>

                  <Checkbox
                    checked={draft.first_time_only}
                    onChange={(e) => setField("first_time_only", e.target.checked)}
                    label="First-time only"
                  />
                </>
              )}

              {notice && (
                <div className={`rounded-2xl px-4 py-3 ${noticeStyles[notice.type]}`}>
                  <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{notice.message}</p>
                </div>
              )}

              <div className="flex items-center justify-end">
                <PrimaryButton
                  type="button"
                  loading={saveMutation.isPending}
                  disabled={saveMutation.isPending}
                  onClick={handleSave}
                >
                  {copy.saveLabel}
                </PrimaryButton>
              </div>
            </>
          )}
        </CardBody>
      )}
    </Card>
  );
};

export default CouponDefaultTemplateCard;
